import {

    collection,
    collectionGroup,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    where,
    limit,
    setDoc,
    serverTimestamp,
    onSnapshot,
    getCountFromServer,
    startAfter

} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { Collections } from "@/helpers/utils/collections";
import { Chat, Message, Participant } from "@/models/chat";
import { Timestamp } from "firebase-admin/firestore";

const chatsCollection = collection(db, Collections.CHATS);

// Utility function to generate a unique chat ID for user's admin chat
export const generateAdminChatId = (userId: string): string => {
    return `admin_${userId}`;
}



export const getOrCreateUserAdminChat = async (userId: string): Promise<Chat> => {
    const adminChatId = generateAdminChatId(userId);

    const chatDoc = await getDoc(doc(chatsCollection, adminChatId));


    if (chatDoc.exists()) {
        return {
            ...chatDoc.data(),
            createdAt: chatDoc.data().createdAt?.toDate() ?? new Date(),
        } as Chat;
    } else {
        const chat: Chat = {
            chatId: adminChatId,
            type: 'support',
            createdAt: new Date(),
            createdBy: userId,
            lastMessage: {
                content: "Welcome to the support chat",
                createdAt: new Date(),
                senderId: "admin",
                senderName: "Admin",
                senderAvatar: "https://images.unsplash.com/photo-1552642986-ccb41e7059e7?q=80&w=3087&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
            }
        };

        await setDoc(doc(chatsCollection, adminChatId), {
            ...chat,
            createdAt: serverTimestamp(),
        });

        // add user to participants
        const participantsCollection = collection(db, `${Collections.CHATS}/${adminChatId}/${Collections.PARTICIPANTS}`);

        const participantRef = doc(participantsCollection, userId);

        const participant: Participant = {
            userId: userId,
            joinedAt: new Date(),
            chatId: adminChatId
        };

        await setDoc(participantRef, {
            ...participant,
            joinedAt: serverTimestamp()
        });

        return chat;
    }

}


export const getUserChatIds = async (userId: string , maxLimit: number = 10): Promise<string[]> => {
    const chatIds: string[] = [];
    const snapshot = await getDocs(query(
        collectionGroup(db, Collections.PARTICIPANTS),
        where("userId", "==", userId),
        limit(maxLimit)
    ));

    snapshot.forEach((doc) => {
        chatIds.push(doc.data().chatId);
    });

    return chatIds;
}



export const getChatsByIds = async (chatIds: string[]): Promise<Chat[]> => {
    const chats: Chat[] = [];
    for (const chatId of chatIds) {

        const chatDoc = await getDoc(doc(chatsCollection, chatId));
        if (chatDoc.exists()) {
            console.log(chatId)

            chats.push({
                ...chatDoc.data(),
                createdAt: chatDoc.data().createdAt?.toDate() ?? new Date(),
                lastMessage: {
                    ...chatDoc.data().lastMessage,
                    createdAt: chatDoc.data().lastMessage?.createdAt?.toDate() ?? new Date(),
                }
            } as Chat
            );



        }
    }


    // Sort chats by last message date
    chats.sort((a, b) => (b.lastMessage?.createdAt ?? new Date()).getTime() - (a.lastMessage?.createdAt ?? new Date()).getTime());
    return chats;
}

export const getRecentMessages = async (chatId: string, maxLimit: number = 10): Promise<Message[]> => {
    const messages: Message[] = [];
    const snapshot = await getDocs(query(
        collection(db, `${Collections.CHATS}/${chatId}/${Collections.MESSAGES}`),
        orderBy("createdAt", "desc"),
        limit(maxLimit)

    ));

    snapshot.forEach((doc) => {
        messages.push({
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() ?? new Date()
        } as Message);
    });

    return messages;
}

export const getTotalUserChatCount = async (userId: string): Promise<number> => {
    const coll = collection(db, `${Collections.USERS}/${userId}/${Collections.CHATS}`);
    const snapshot = await getCountFromServer(coll);

    return snapshot.data().count;
}

export const listenToChats = (userId: string,  callback: (messages: Chat[]) => void,  maxLimit:number = 10) => {

    const unsubscribe = onSnapshot(
        query(
            collection(db, `${Collections.USERS}/${userId}/${Collections.CHATS}`),
            orderBy("lastMessage.createdAt", "desc"),
            limit(maxLimit)
        ),
        (snapshot) => {
            const chats: Chat[] = [];
            snapshot.forEach((doc) => {
                chats.push({
                    chatId: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate() ?? new Date(),
                    lastMessage: {
                        ...doc.data().lastMessage,
                        createdAt: doc.data().lastMessage?.createdAt?.toDate() ?? new Date(),
                    }
                } as Chat);
            });
            callback(chats);
        }
    );
    return unsubscribe;
}

export const fetchNextPageOfChats = async (userId: string, lastVisible: Timestamp, maxLimit: number = 10): Promise<Chat[]> => {
    const chatsQuery = query(
        collection(db, `${Collections.USERS}/${userId}/${Collections.CHATS}`),
        orderBy("lastMessage.createdAt", "desc"),
        startAfter(lastVisible),
        limit(maxLimit)
    );

    const snapshot = await getDocs(chatsQuery);
    const chats: Chat[] = [];

    snapshot.forEach((doc) => {
        chats.push({
            chatId: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() ?? new Date(),
            lastMessage: {
                ...doc.data().lastMessage,
                createdAt: doc.data().lastMessage?.createdAt?.toDate() ?? new Date(),
            }
        } as Chat);
    });

    return chats;
}



export const createChat = async (chat: Chat, initialParticipants: Participant[]): Promise<void> => {
    const chatRef = doc(chatsCollection, chat.chatId);

    await setDoc(chatRef, {
        ...chat,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMesssage: "Chat created"
    });


    const participantsCollection = collection(db, `${Collections.CHATS}/${chat.chatId}/${Collections.PARTICIPANTS}`);

    for (const participant of initialParticipants) {
        const participantRef = doc(participantsCollection, participant.userId);
        await setDoc(participantRef, {
            ...participant,
            joinedAt: serverTimestamp()
        });
    }
}

export const getAllParticipants = async (chatId: string): Promise<Participant[]> => {
    const participants: Participant[] = [];
    const snapshot = await getDocs(collection(db, `${Collections.CHATS}/${chatId}/${Collections.PARTICIPANTS}`));

    snapshot.forEach((doc) => {
        participants.push({
            ...doc.data(),
            joinedAt: doc.data().joinedAt?.toDate() ?? new Date()
        } as Participant);
    });

    return participants;
}

export const sendMessageToDb = async (chat: Chat, message: Message): Promise<void> => {
    const chatId = chat.chatId;
    console.log("sending message to chat", chat.type, chatId, message)
    switch (chat.type) {
        case 'direct':
            return sendDirectMessage(chatId, message);
        case 'group':
            return sendGroupMessage(chatId, message);
        case 'support':
            return sendSupportMessage(chatId, message);
        default:
            throw new Error("Invalid chat type");
    }
}

const sendDirectMessage = async (chatId: string, message: Message): Promise<void> => {
    const messageRef = doc(collection(db, `${Collections.CHATS}/${chatId}/${Collections.MESSAGES}`));
    await setDoc(messageRef, {
        ...message,
        createdAt: serverTimestamp()
    });

    await updateChatLastMessage(chatId, message);
}

const sendSupportMessage = async (chatId: string, message: Message): Promise<void> => {
    const messageRef = doc(collection(db, `${Collections.CHATS}/${chatId}/${Collections.MESSAGES}`));
    await setDoc(messageRef, {
        ...message,
        createdAt: serverTimestamp()
    });

    await updateChatLastMessage(chatId, message);
}

const sendGroupMessage = async (chatId: string, message: Message): Promise<void> => {
    const messageRef = doc(collection(db, `${Collections.CHATS}/${chatId}/${Collections.MESSAGES}`));
    await setDoc(messageRef, {
        ...message,
        createdAt: serverTimestamp()
    });

    await updateChatLastMessage(chatId, message);
}

const updateChatLastMessage = async (chatId: string, message: Message): Promise<void> => {
    await setDoc(doc(chatsCollection, chatId), {
        lastMessage: message,
    }, { merge: true });
}